// src/api/features/bookings/api.ts

import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type {
  AdminBookingResult,
  AdminBookingDetailResult,
  AdminListBookingsQuery,
  PaginatedAdminBookings,
  AdminTourBookingTabCounts,
  AdminFlightBookingTabCounts,
  AdminHotelBookingTabCounts,
  AdminUpdateStatusBody,
  AdminUpdatePaxBody,
  AdminReplaceTravelersBody,
  AdminSetPaymentLinkBody,
  AdminGeneratePaymentLinkBody,
  AdminTravelPlanResult,
  AdminUpsertTravelPlanBody,
  AdminGenerateVoucherBody,
  AdminGenerateVoucherResult,
  AdminBulkDeleteResult,
} from "./types";

/* =========================
 * Helpers
 * ========================= */

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrapData<T>(res: any): T {
  if (!res || typeof res !== "object") return res as T;
  if ("ok" in res && "data" in res) return (res as ApiEnvelope<T>).data;
  if ("success" in res && "data" in res) return (res as { success: boolean; data: T }).data;
  return res as T;
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

function normalizeNumericId(fallback: number | string, maybe: any): number {
  if (typeof maybe === "number" && Number.isFinite(maybe)) return maybe;
  const n = Number(maybe);
  if (Number.isFinite(n)) return n;
  return Number(fallback);
}

function normalizeTravelPlan(bookingId: number | string, data: any): AdminTravelPlanResult {
  return {
    ...(data as any),
    bookingId: normalizeNumericId(bookingId, (data as any)?.bookingId),
    segmentsJson: Array.isArray((data as any)?.segmentsJson) ? (data as any).segmentsJson : [],
  } as AdminTravelPlanResult;
}

function asTrimmedString(v: any): string | undefined {
  if (v === undefined || v === null) return undefined;
  const s = String(v).trim();
  return s ? s : undefined;
}

/* =========================
 * URLs (constants)
 * ========================= */

const ADMIN = PATHS.bookingsAdmin;

/* =========================
 * Endpoints — ADMIN BOOKINGS
 * ========================= */

/** 🔹 Admin: List bookings (paginated) */
export async function adminListBookings(query: AdminListBookingsQuery = {}): Promise<PaginatedAdminBookings> {
  const safePage = Math.max(1, Number(query.page ?? 1));
  const rawPerPage = Number(query.perPage ?? 10);
  const safePerPage = Math.max(1, Math.min(100, Number.isFinite(rawPerPage) ? rawPerPage : 10));

  const url = `${ADMIN.list}${qs({
    page: safePage,
    perPage: safePerPage,

    q: query.q,
    bookingCode: query.bookingCode?.trim() || undefined,
    customer: query.customer?.trim() || undefined,
    status: query.status,
    itemType: query.itemType,

    fromDate: query.fromDate,
    toDate: query.toDate,

    tourId: query.tourId,
    customerUserId: query.customerUserId,

    sortBy: query.sortBy,
    orderBy: query.orderBy,
  })}`;

  const res = await request<any>({ method: "GET", url });

  // A) { ok, data: { data: [], meta: {} } }
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const outer = res as ApiEnvelope<any>;
    const inner = outer.data;

    if (inner && typeof inner === "object" && "data" in inner) {
      const items = Array.isArray(inner.data) ? inner.data : [];
      const meta = inner.meta ?? {};

      const total = Number(meta.total ?? items.length ?? 0);
      const totalPages =
        meta.totalPages != null ? Number(meta.totalPages) : Math.max(1, Math.ceil(total / safePerPage));

      return {
        items,
        page: Number(meta.page ?? safePage),
        perPage: Number(meta.perPage ?? meta.pageSize ?? safePerPage),
        total,
        totalPages,
      };
    }

    // B) { ok, data: [], meta: {} }
    const items = Array.isArray(outer.data) ? outer.data : [];
    const meta = outer.meta ?? {};

    const total = Number(meta.total ?? items.length ?? 0);
    const totalPages =
      meta.totalPages != null ? Number(meta.totalPages) : Math.max(1, Math.ceil(total / safePerPage));

    return {
      items,
      page: Number(meta.page ?? safePage),
      perPage: Number(meta.perPage ?? meta.pageSize ?? safePerPage),
      total,
      totalPages,
    };
  }

  // C) assume already PaginatedAdminBookings
  return res as PaginatedAdminBookings;
}

/** 🔹 Admin: Search bookings (same schema as list) */
export async function adminSearchBookings(query: AdminListBookingsQuery = {}): Promise<PaginatedAdminBookings> {
  const safePage = Math.max(1, Number(query.page ?? 1));
  const rawPerPage = Number(query.perPage ?? 10);
  const safePerPage = Math.max(1, Math.min(100, Number.isFinite(rawPerPage) ? rawPerPage : 10));

  const url = `${ADMIN.search}${qs({
    page: safePage,
    perPage: safePerPage,

    q: query.q,
    bookingCode: query.bookingCode?.trim() || undefined,
    customer: query.customer?.trim() || undefined,
    status: query.status,
    itemType: query.itemType,

    fromDate: query.fromDate,
    toDate: query.toDate,

    tourId: query.tourId,
    customerUserId: query.customerUserId,

    sortBy: query.sortBy,
    orderBy: query.orderBy,
  })}`;

  const res = await request<any>({ method: "GET", url });

  // Normalize using the same logic as list
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const outer = res as ApiEnvelope<any>;
    const inner = outer.data;

    if (inner && typeof inner === "object" && "data" in inner) {
      const items = Array.isArray(inner.data) ? inner.data : [];
      const meta = inner.meta ?? {};

      const total = Number(meta.total ?? items.length ?? 0);
      const totalPages =
        meta.totalPages != null ? Number(meta.totalPages) : Math.max(1, Math.ceil(total / safePerPage));

      return {
        items,
        page: Number(meta.page ?? safePage),
        perPage: Number(meta.perPage ?? meta.pageSize ?? safePerPage),
        total,
        totalPages,
      };
    }

    const items = Array.isArray(outer.data) ? outer.data : [];
    const meta = outer.meta ?? {};

    const total = Number(meta.total ?? items.length ?? 0);
    const totalPages =
      meta.totalPages != null ? Number(meta.totalPages) : Math.max(1, Math.ceil(total / safePerPage));

    return {
      items,
      page: Number(meta.page ?? safePage),
      perPage: Number(meta.perPage ?? meta.pageSize ?? safePerPage),
      total,
      totalPages,
    };
  }

  return res as PaginatedAdminBookings;
}

export async function adminGetTourTabCounts(): Promise<AdminTourBookingTabCounts> {
  const res = await request<any>({
    method: "GET",
    url: ADMIN.tourTabCounts,
  });

  return unwrapData<AdminTourBookingTabCounts>(res);
}

export async function adminGetFlightTabCounts(): Promise<AdminFlightBookingTabCounts> {
  const res = await request<any>({
    method: "GET",
    url: ADMIN.flightTabCounts,
  });

  return unwrapData<AdminFlightBookingTabCounts>(res);
}

export async function adminGetHotelTabCounts(): Promise<AdminHotelBookingTabCounts> {
  const res = await request<any>({
    method: "GET",
    url: ADMIN.hotelTabCounts,
  });

  return unwrapData<AdminHotelBookingTabCounts>(res);
}

/** 🔹 Admin: Get booking detail by id
 * Backend returns: { ok, data: { booking, items, travelers, selection } }
 */
export async function adminGetBookingById(id: number | string): Promise<AdminBookingDetailResult> {
  const res = await request<any>({
    method: "GET",
    url: ADMIN.byId(id),
  });

  const data = unwrapData<AdminBookingDetailResult>(res);

  return {
    booking: (data as any)?.booking ?? (data as any),
    items: Array.isArray((data as any)?.items) ? (data as any).items : [],
    travelers: Array.isArray((data as any)?.travelers) ? (data as any).travelers : [],
    selection: (data as any)?.selection ?? null,
    flight: (data as any)?.flight ?? null,
  };
}

/** 🔹 Admin: Update status */
export async function adminUpdateBookingStatus(
  id: number | string,
  body: AdminUpdateStatusBody
): Promise<AdminBookingResult> {
  const dto: any = { ...body };
  if (dto.status != null) dto.status = String(dto.status).trim();

  const res = await request<any>({
    method: "PATCH",
    url: ADMIN.updateStatus(id),
    data: dto,
  });

  return unwrapData<AdminBookingResult>(res);
}

/** 🔹 Admin: Edit booking basic fields */
export async function adminEditBooking(id: number | string, body: AdminUpdatePaxBody): Promise<AdminBookingResult> {
  const dto: any = { ...body };
  if (dto.allocKey != null) dto.allocKey = String(dto.allocKey).trim();

  const res = await request<any>({
    method: "PATCH",
    url: ADMIN.edit(id),
    data: dto,
  });

  return unwrapData<AdminBookingResult>(res);
}

/** 🔹 Admin: Replace travelers */
export async function adminReplaceTravelers(
  id: number | string,
  body: AdminReplaceTravelersBody
): Promise<AdminBookingResult> {
  const res = await request<any>({
    method: "PUT",
    url: ADMIN.travelers(id),
    data: body,
  });

  return unwrapData<AdminBookingResult>(res);
}

/** 🔹 Admin: Start checkout (legacy / optional) */
export async function adminStartCheckout(
  id: number | string,
  body: Record<string, any> = {}
): Promise<AdminBookingResult> {
  const res = await request<any>({
    method: "POST",
    url: ADMIN.checkout(id),
    data: body,
  });

  return unwrapData<AdminBookingResult>(res);
}

/**
 * ✅ Admin: Generate HDFC payment link
 * POST /bookings/admin/:id/payment-link/generate
 *
 * Backend returns: { ok, data: { booking, payment, paymentLinkUrl } }
 * We normalize into: { booking, paymentId, paymentLinkUrl }
 */
export async function adminGeneratePaymentLink(
  id: number | string,
  body: AdminGeneratePaymentLinkBody = {}
): Promise<{ booking: AdminBookingResult; paymentId: number; paymentLinkUrl: string }> {
  const dto: any = { ...body };

  // defaults
  if (dto.provider == null) dto.provider = "HDFC";
  if (dto.kind != null) dto.kind = String(dto.kind).trim();
  if (dto.provider != null) dto.provider = String(dto.provider).trim();

  if (dto.paymentId !== undefined) {
    dto.paymentId = dto.paymentId == null ? null : Number(dto.paymentId);
    if (dto.paymentId != null && !Number.isFinite(dto.paymentId)) dto.paymentId = null;
  }

  if (dto.expiresAt != null) dto.expiresAt = asTrimmedString(dto.expiresAt) ?? null;
  if (dto.note != null) dto.note = String(dto.note);

  const res = await request<any>({
    method: "POST",
    url: ADMIN.generatePaymentLink(id),
    data: dto,
  });

  const data = unwrapData<any>(res);

  const booking = (data as any)?.booking ?? (data as any)?.data?.booking ?? null;
  const payment = (data as any)?.payment ?? (data as any)?.data?.payment ?? null;

  const paymentLinkUrl =
    asTrimmedString((data as any)?.paymentLinkUrl) ??
    asTrimmedString((data as any)?.data?.paymentLinkUrl) ??
    asTrimmedString((data as any)?.paymentLink) ??
    asTrimmedString((payment as any)?.providerCheckoutUrl) ??
    null;

  if (!booking) throw new Error("PAYMENT_LINK_RESPONSE_MISSING_BOOKING");

  const paymentId =
    normalizeNumericId(0, (payment as any)?.id ?? (data as any)?.paymentId ?? (data as any)?.data?.paymentId);

  if (!paymentId) throw new Error("PAYMENT_LINK_RESPONSE_MISSING_PAYMENT_ID");
  if (!paymentLinkUrl) throw new Error("PAYMENT_LINK_RESPONSE_MISSING_URL");

  return {
    booking: booking as AdminBookingResult,
    paymentId,
    paymentLinkUrl,
  };
}

/**
 * Kept for backwards compat (returns booking only).
 * Prefer adminGeneratePaymentLink above (it returns paymentId + url too).
 */
export async function adminGenerateHdfcPaymentLink(
  id: number | string,
  body: AdminGeneratePaymentLinkBody
): Promise<AdminBookingResult> {
  const dto = {
    provider: body.provider,
    kind: body.kind,
    expiresAt: body.expiresAt ?? null,
    note: body.note ?? null,
  };

  const res = await request<any>({
    method: "POST",
    url: ADMIN.generatePaymentLink(id),
    data: { payment: dto }, // ✅ wrap inside payment
  });

  return unwrapData<AdminBookingResult>(res);
}

/** 🔹 Admin: Set payment link (manual override OR attach hosted link)
 * PATCH /bookings/admin/:id/payment-link
 *
 * IMPORTANT:
 * - backend expects `paymentLink` (NOT paymentLinkUrl)
 */
export async function adminSetPaymentLink(id: number | string, body: AdminSetPaymentLinkBody): Promise<AdminBookingResult> {
  const dto: any = { ...body };

  // Accept old UI key if any code still sends it, but convert to backend key
  if (dto.paymentLink == null && dto.paymentLinkUrl != null) {
    dto.paymentLink = String(dto.paymentLinkUrl).trim();
    delete dto.paymentLinkUrl;
  }

  if (dto.paymentLink != null) dto.paymentLink = String(dto.paymentLink).trim();
  if (dto.provider != null) dto.provider = String(dto.provider).trim();
  if (dto.kind != null) dto.kind = String(dto.kind).trim();

  if (dto.paymentId !== undefined) {
    dto.paymentId = dto.paymentId == null ? null : Number(dto.paymentId);
    if (dto.paymentId != null && !Number.isFinite(dto.paymentId)) dto.paymentId = null;
  }

  if (dto.expiresAt != null) dto.expiresAt = asTrimmedString(dto.expiresAt) ?? null;
  if (dto.note != null) dto.note = String(dto.note);

  const res = await request<any>({
    method: "PATCH",
    url: ADMIN.paymentLink(id),
    data: dto,
  });

  return unwrapData<AdminBookingResult>(res);
}

/** 🔹 Admin: Generate voucher (opens PDF link in UI)
 * POST /bookings/my/:id/voucher/generate (your example path)
 *
 * Backend returns:
 * { ok: true, message, data: { ok: true, voucherId, voucherNumber, version, url, key } }
 */
export async function adminGenerateVoucher(
  bookingId: number | string,
  body: AdminGenerateVoucherBody = {}
): Promise<AdminGenerateVoucherResult> {
  const dto: any = { ...body };

  if (dto.forceReissue != null) dto.forceReissue = !!dto.forceReissue;

  // ✅ allow passing vendor ref (optional)
  if ("vendorRefNumber" in dto) {
    const s = String(dto.vendorRefNumber ?? "").trim();
    dto.vendorRefNumber = s ? s : null;
  }

  const url = PATHS.bookingsAdmin.voucherGenerate(bookingId);

  const res = await request<any>({
    method: "POST",
    url,
    data: dto,
  });

  // unwrapData returns the inner "data" field (usually)
  const data = unwrapData<any>(res);

  // Be defensive in case backend nests again
  const d =
    (data as any)?.data && typeof (data as any).data === "object"
      ? (data as any).data
      : data;

  const vendorRef = String((d as any)?.vendorRefNumber ?? "").trim();

  const result: AdminGenerateVoucherResult = {
    ok: !!(d as any)?.ok,
    voucherId: normalizeNumericId(0, (d as any)?.voucherId),
    voucherNumber: String((d as any)?.voucherNumber ?? "").trim(),
    vendorRefNumber: vendorRef ? vendorRef : null, // ✅ NEW
    version: normalizeNumericId(0, (d as any)?.version),
    url: String((d as any)?.url ?? "").trim() || null,
    key: String((d as any)?.key ?? "").trim() || null,
  };

  if (!result.voucherId) throw new Error("VOUCHER_RESPONSE_MISSING_VOUCHER_ID");
  if (!result.voucherNumber) throw new Error("VOUCHER_RESPONSE_MISSING_VOUCHER_NUMBER");

  return result;
}

/** 🔹 Admin: Delete booking (soft delete). Reason is required by the API. */
export async function adminDeleteBooking(
  id: number | string,
  reason: string
): Promise<{ message?: string } | AdminBookingResult | null> {
  const res = await request<any>({
    method: "DELETE",
    url: ADMIN.delete(id),
    data: { reason },
  });

  if (res && typeof res === "object" && "ok" in res) {
    if ("message" in res && !("data" in res)) {
      return { message: String((res as any).message ?? "") };
    }
    return unwrapData<any>(res);
  }

  return res as any;
}

/** 🔹 Admin: Bulk delete bookings (soft delete). Partial success is expected. */
export async function adminBulkDeleteBookings(
  bookingIds: number[],
  reason: string
): Promise<AdminBulkDeleteResult> {
  const res = await request<any>({
    method: "POST",
    url: ADMIN.bulkDelete,
    data: { bookingIds, reason },
  });

  const data =
    res && typeof res === "object" && "ok" in res ? unwrapData<any>(res) : res;

  return {
    requested: Number(data?.requested ?? bookingIds.length),
    deleted: Array.isArray(data?.deleted) ? data.deleted.map(Number) : [],
    skipped: Array.isArray(data?.skipped) ? data.skipped : [],
  };
}

/* =========================
 * Endpoints — ADMIN TRAVEL PLAN
 * ========================= */

/** 🔹 Admin: Get travel plan (auto-creates if missing) */
export async function adminGetTravelPlan(bookingId: number | string): Promise<AdminTravelPlanResult> {
  const res = await request<any>({
    method: "GET",
    url: ADMIN.travelPlan(bookingId),
  });

  const data = unwrapData<AdminTravelPlanResult>(res);
  return normalizeTravelPlan(bookingId, data);
}

/** 🔹 Admin: Upsert travel plan (PUT /admin/:bookingId/travel-plan) */
export async function adminUpsertTravelPlan(
  bookingId: number | string,
  body: AdminUpsertTravelPlanBody
): Promise<AdminTravelPlanResult> {
  const dto: any = { ...body };

  if (dto.status != null) dto.status = String(dto.status).trim();

  // backend expects array, never null
  if ("segmentsJson" in dto && dto.segmentsJson == null) dto.segmentsJson = [];

  const res = await request<any>({
    method: "PUT",
    url: ADMIN.travelPlan(bookingId),
    data: dto,
  });

  const data = unwrapData<AdminTravelPlanResult>(res);
  return normalizeTravelPlan(bookingId, data);
}

/** 🔹 Admin: Delete travel plan (soft delete) */
export async function adminDeleteTravelPlan(
  bookingId: number | string
): Promise<{ message?: string } | AdminTravelPlanResult | null> {
  const res = await request<any>({
    method: "DELETE",
    url: ADMIN.travelPlan(bookingId),
  });

  // Most common patterns:
  // 1) { ok: true, message: "..." }   (no data)
  // 2) { ok: true, data: {...plan...} }
  // 3) raw object / message
  if (res && typeof res === "object" && "ok" in res) {
    const okVal = (res as any).ok;
    if (okVal === false) {
      return { message: String((res as any).message ?? "FAILED_TO_DELETE_TRAVEL_PLAN") };
    }

    if ("data" in res) {
      const data = unwrapData<any>(res);
      if (data && typeof data === "object") {
        return normalizeTravelPlan(bookingId, data);
      }
      return data as any;
    }

    if ("message" in res) {
      return { message: String((res as any).message ?? "") };
    }
  }

  return res as any;
}
